/**
 * The live alarm stream.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY `fetch` AND NOT `EventSource`
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `EventSource` is the obvious tool and cannot set headers. The only way to authenticate
 * it is a token in the query string, which lands in the access log, in every proxy log
 * between here and the API, and in `Referer` on any subsequent navigation. A bearer token
 * with an hour of life in a log file is a credential leak with a slow fuse.
 *
 * `fetch` with a `ReadableStream` reads the same protocol and carries the token in the
 * header like every other request in this client. The cost is that reconnection is ours to
 * implement — which is the loop below, and is about twenty lines.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT ARRIVES, AND WHAT THE CLIENT MUST NOT ASSUME
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The server pushes a POINTER to an alarm, not the alarm — enough for a toast and a
 * notification row, and nothing more. Anything richer is fetched over the ordinary
 * authenticated API, where the tenant predicate applies.
 *
 * DELIVERY IS NOT GUARANTEED. Postgres `NOTIFY` is fire-and-forget: events raised while the
 * connection is down are gone, not queued. So this stream is a way to be told SOONER, never
 * the source of truth — every screen that shows alarms still fetches them, and a reconnect
 * invalidates those queries so the list reconciles. A UI that treated the stream as the
 * record would silently drift every time a network blipped.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { rateLimitedFor } from './backoff';
import { API_BASE } from './client';
import { createRefreshCoalescer } from './refresh-coalescer';
import { supabase } from '@/lib/supabase';

/** `reminder` (Lumen-BE 0091): addressed to this person — the alarm needs an owner, or is waiting on them. */
export type AlarmEventKind = 'opened' | 'closed' | 'reopened' | 'changed' | 'reminder';

export interface LiveAlarm {
  id: string;
  site: string;
  device: string;
  name: string;
  severity: string;
  state: string;
  openedAt: string;
  closedAt: string | null;
  event: AlarmEventKind;
  /** For a `reminder`: `unassigned` (you are the shift lead) or `untouched` (it is yours). */
  rule?: 'unassigned' | 'untouched';
  /** Stamped on arrival. The server's `openedAt` is when the DEVICE saw it. */
  receivedAt: number;
}

export type StreamState = 'connecting' | 'live' | 'offline';

/** Backoff between reconnection attempts. Capped so a long outage does not park it. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000] as const;

/**
 * How a burst of events becomes one refresh (`refresh-coalescer.ts`): quiet for 400 ms, and
 * never more than 2 s after the burst's first event. A toast is not delayed by it, only the refetch.
 */
const REFRESH_QUIET_MS = 400;
const REFRESH_MAX_WAIT_MS = 2_000;

/**
 * Read the alarm stream for as long as the component is mounted.
 *
 * One connection per PROCESS, not per component: the hook is used once, high in the tree,
 * and everything else reads the store it fills. Two components calling this would open two
 * streams and double every toast.
 */
export function useAlarmStream(options: {
  onAlarm: (a: LiveAlarm) => void;
  enabled: boolean;
}): StreamState {
  const { onAlarm, enabled } = options;
  const [state, setState] = useState<StreamState>('connecting');
  const qc = useQueryClient();

  /*
   * The callback in a ref, so a re-render of the parent does not tear down the stream.
   * `onAlarm` is almost always an inline closure, so having it in the effect's dependency
   * array would reconnect on every render — which, with a 1s backoff, is a reconnect loop
   * that looks like a working feature until somebody watches the network panel.
   */
  const onAlarmRef = useRef(onAlarm);
  onAlarmRef.current = onAlarm;

  useEffect(() => {
    if (!enabled) {
      setState('offline');
      return;
    }

    let cancelled = false;
    let attempt = 0;
    const controller = new AbortController();
    /*
     * EVERY ALARM QUERY, ONCE PER BURST. Every alarm hook keys under `'alarms'` (`keys` in
     * `queries.ts`: lists, the summary, triage), so one invalidate-by-prefix reaches the ten-plus
     * screens that show alarms without a cache-patcher per shape. It used to run once per EVENT,
     * and a payload moving N alarms then cost every mounted query N refetches — the 429 storm
     * `refresh-coalescer.ts` describes. Inactive queries are only marked stale, as before.
     */
    const refresh = createRefreshCoalescer(() => void qc.invalidateQueries({ queryKey: ['alarms'] }), {
      quietMs: REFRESH_QUIET_MS,
      maxWaitMs: REFRESH_MAX_WAIT_MS,
      holdFor: () => rateLimitedFor(),
    });

    const run = async (): Promise<void> => {
      while (!cancelled) {
        try {
          setState('connecting');
          const { data } = await supabase.auth.getSession();
          const token = data.session?.access_token;
          if (token === undefined) {
            /* Signed out. Not an error and not worth retrying quickly — the auth provider
               will remount this when a session appears. */
            setState('offline');
            return;
          }

          /*
           * `/api/v1` is spelled out because `API_BASE` is the ORIGIN only — `request()`
           * appends the version prefix itself, and this hand-rolled fetch does not go
           * through it. Missing it produced a 404 every reconnect, which the backoff then
           * retried politely forever: a feature that looked implemented and never fired.
           */
          const res = await fetch(`${API_BASE}/api/v1/events/alarms`, {
            headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
            signal: controller.signal,
          });

          if (!res.ok || res.body === null) {
            throw new Error(`stream refused: ${res.status}`);
          }

          attempt = 0;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            /*
             * SSE frames are separated by a blank line, and a chunk can split one anywhere
             * — including mid-JSON. So the buffer is only consumed up to the last complete
             * frame and the remainder is kept. Parsing per chunk instead is the classic
             * bug here: it works on a quiet stream and drops events under load, which is
             * exactly when they matter.
             */
            let split = buffer.indexOf('\n\n');
            while (split !== -1) {
              const frame = buffer.slice(0, split);
              buffer = buffer.slice(split + 2);
              split = buffer.indexOf('\n\n');

              let evName = 'message';
              const dataLines: string[] = [];
              for (const line of frame.split('\n')) {
                if (line.startsWith(':')) continue; // heartbeat comment
                if (line.startsWith('event:')) evName = line.slice(6).trim();
                else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
              }
              if (dataLines.length === 0) continue;

              if (evName === 'ready') {
                setState('live');
                /*
                 * RECONCILE ON EVERY CONNECT, not only on the first.
                 *
                 * Events raised while the stream was down are gone — `NOTIFY` does not
                 * queue. Invalidating here is what stops the board drifting after a
                 * network blip: the stream makes us fast, the fetch keeps us correct.
                 */
                refresh.poke();
                continue;
              }

              /* A reminder is the server addressing THIS person (Lumen-BE 0091); it rides the same feed. */
              if (evName !== 'alarm' && evName !== 'reminder') continue;
              try {
                const p = JSON.parse(dataLines.join('\n')) as Omit<LiveAlarm, 'receivedAt'>;
                onAlarmRef.current({ ...p, ...(evName === 'reminder' ? { event: 'reminder' as const } : {}), receivedAt: Date.now() });
                /*
                 * The same refresh as `ready`, on every event too — coalesced with the rest of
                 * its burst. This is what let the fixed-interval polls on the alarm hooks be
                 * deleted: the pointer this stream carries is enough to know to go re-fetch, and
                 * `NOTIFY`'s no-queue delivery just means a dropped connection still needs the
                 * `ready` reconciliation above to catch up.
                 */
                refresh.poke();
              } catch {
                /* A frame we cannot parse is one event lost, not a reason to drop the
                   stream — the next one is probably fine. */
              }
            }
          }

          /* A clean end of stream is a server restart or a proxy timeout. Reconnect. */
          if (!cancelled) throw new Error('stream ended');
        } catch {
          /* The error itself is not logged: a stream that drops is ordinary — a laptop
             sleeping, a proxy timing out, a deploy restarting the API — and logging each one
             would fill the console with normal behaviour. The state below is what the UI
             shows, and the bell says `offline` when it matters. */
          if (cancelled || controller.signal.aborted) return;
          setState('offline');
          const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]!;
          attempt += 1;
          await new Promise((r) => setTimeout(r, wait));
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      refresh.cancel();
      controller.abort();
    };
  }, [enabled, qc]);

  return state;
}
